import { Injectable } from '@angular/core';
import { GoogleGenerativeAI, GenerativeModel } from '@google/generative-ai';
import { environment } from '../../environments/environment';
import { Observable, from, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface GeminiMessage {
  role: 'user' | 'model';
  parts: string;
}

// Models tried in order for text generation. The first is the normal one; the rest are fallbacks
// for when it is overloaded (Gemini answers "503 ... This model is currently experiencing high
// demand"), which happens to a single model at a time, so another one usually still answers.
const TEXT_MODELS = ['gemini-3.6-flash', 'gemini-2.5-flash', 'gemini-2.5-pro'];
// Waits before retrying the primary model when it is overloaded; then the fallbacks get a turn.
const OVERLOAD_RETRY_DELAYS_MS = [1500, 4000];

/** A temporary capacity error (overloaded, unavailable, rate-limited), worth retrying. */
function isOverloadError(error: any): boolean {
  const status = Number(error?.status);
  if (status === 503 || status === 429 || status === 500) return true;
  return /\b(503|429)\b|overloaded|high demand|unavailable|try again later|resource.?exhausted/i
    .test(String(error?.message || error || ''));
}

/** The model itself can't be used with this key (unknown or retired), so skip to the next one. */
function isModelMissingError(error: any): boolean {
  return Number(error?.status) === 404 || /\b404\b|not found|is not supported/i.test(String(error?.message || ''));
}

export interface GeminiChatOptions {
  temperature?: number;
  topK?: number;
  topP?: number;
  maxOutputTokens?: number;
}

@Injectable({
  providedIn: 'root'
})
export class GeminiService {
  private genAI: GoogleGenerativeAI;
  private model: GenerativeModel;

  constructor() {
    if (!environment.geminiApiKey) {
      console.warn('Gemini API key not found in environment configuration');
    }
    this.genAI = new GoogleGenerativeAI(environment.geminiApiKey || '');
    this.model = this.genAI.getGenerativeModel({ model: 'gemini-3.6-flash' });
  }

  /**
   * Generate content from a text prompt
   * @param prompt The text prompt to send to Gemini
   * @returns Observable with the generated text response
   */
  generateContent(prompt: string): Observable<string> {
    return from(this.generateWithFallback(prompt)).pipe(
      catchError(error => {
        console.error('Error generating content:', error);
        return throwError(() => new Error(error.message || 'Failed to generate content'));
      })
    );
  }

  /**
   * Generates text, riding out a busy model: an overloaded primary model is retried after a
   * short wait (twice), then each fallback model in TEXT_MODELS is tried once. Any other error
   * from the primary (bad key, blocked prompt…) is thrown straight away, since retrying or
   * switching model wouldn't fix it. If every model is busy, the error says so in plain words.
   */
  private async generateWithFallback(request: string | Array<string | any>): Promise<string> {
    let lastError: any = null;
    for (let m = 0; m < TEXT_MODELS.length; m++) {
      const model = m === 0 ? this.model : this.genAI.getGenerativeModel({ model: TEXT_MODELS[m] });
      const attempts = m === 0 ? OVERLOAD_RETRY_DELAYS_MS.length + 1 : 1;
      for (let attempt = 0; attempt < attempts; attempt++) {
        try {
          const result = await model.generateContent(request as any);
          if (m > 0) console.warn(`Gemini: ${TEXT_MODELS[0]} was busy; answered by ${TEXT_MODELS[m]}`);
          return result.response.text();
        } catch (error: any) {
          lastError = error;
          if (isOverloadError(error)) {
            if (attempt < attempts - 1) await new Promise(r => setTimeout(r, OVERLOAD_RETRY_DELAYS_MS[attempt]));
            continue;
          }
          if (m > 0 && isModelMissingError(error)) break; // this fallback isn't available; try the next
          throw error;
        }
      }
    }
    throw isOverloadError(lastError)
      ? new Error('The AI service is very busy right now. Please wait a minute and try again.')
      : lastError;
  }

  /**
   * Generate a large document (full HTML report) using gemini-2.5-pro with high output tokens.
   */
  generateLargeContent(prompt: string): Observable<string> {
    const proModel = this.genAI.getGenerativeModel({
      model: 'gemini-2.5-pro',
      generationConfig: { maxOutputTokens: 65536 } as any,
    });
    return from(proModel.generateContent(prompt)).pipe(
      map(result => result.response.text()),
      catchError(error => {
        console.error('Error generating large content:', error);
        return throwError(() => new Error(error.message || 'Failed to generate content'));
      })
    );
  }

  /**
   * Generate content with streaming response
   * @param prompt The text prompt to send to Gemini
   * @returns Observable with the generated text response
   */
  generateContentStream(prompt: string): Observable<string> {
    return new Observable(subscriber => {
      this.model.generateContentStream(prompt)
        .then(async (result) => {
          let fullText = '';
          for await (const chunk of result.stream) {
            const chunkText = chunk.text();
            fullText += chunkText;
            subscriber.next(fullText);
          }
          subscriber.complete();
        })
        .catch(error => {
          console.error('Error generating content stream:', error);
          subscriber.error(error);
        });
    });
  }

  /**
   * Start a chat session with Gemini
   * @param history Optional chat history to initialize the chat
   * @param options Optional generation configuration
   * @returns Observable with chat response
   */
  chat(
    message: string,
    history: GeminiMessage[] = [],
    options?: GeminiChatOptions
  ): Observable<string> {
    const generationConfig = {
      temperature: options?.temperature ?? 0.9,
      topK: options?.topK ?? 40,
      topP: options?.topP ?? 1,
      maxOutputTokens: options?.maxOutputTokens ?? 2048,
    };

    const chat = this.model.startChat({
      history: history.map(msg => ({
        role: msg.role,
        parts: [{ text: msg.parts }]
      })),
      generationConfig
    });

    return from(chat.sendMessage(message)).pipe(
      map(result => result.response.text()),
      catchError(error => {
        console.error('Error in chat:', error);
        return throwError(() => new Error(error.message || 'Failed to send chat message'));
      })
    );
  }

  /**
   * Analyze an image with text prompt
   * @param prompt Text prompt describing what to analyze
   * @param imageData Base64 encoded image or image parts
   * @returns Observable with analysis result
   */
  analyzeImage(prompt: string, imageData: string): Observable<string> {
    const visionModel = this.genAI.getGenerativeModel({ model: 'gemini-3.6-flash' });

    // Remove data URL prefix if present
    const base64Data = imageData.replace(/^data:image\/[a-z]+;base64,/, '');

    const imageParts = [{
      inlineData: {
        data: base64Data,
        mimeType: 'image/jpeg'
      }
    }];

    return from(visionModel.generateContent([prompt, ...imageParts])).pipe(
      map(result => result.response.text()),
      catchError(error => {
        console.error('Error analyzing image:', error);
        return throwError(() => new Error(error.message || 'Failed to analyze image'));
      })
    );
  }

  /**
   * Count tokens in a prompt
   * @param prompt The text to count tokens for
   * @returns Observable with token count
   */
  countTokens(prompt: string): Observable<number> {
    return from(this.model.countTokens(prompt)).pipe(
      map(result => result.totalTokens),
      catchError(error => {
        console.error('Error counting tokens:', error);
        return throwError(() => new Error(error.message || 'Failed to count tokens'));
      })
    );
  }

  /**
   * Generate structured JSON response
   * @param prompt The prompt requesting JSON output
   * @returns Observable with parsed JSON response
   */
  generateJSON<T = any>(prompt: string): Observable<T> {
    const jsonPrompt = `${prompt}\n\nPlease respond ONLY with valid JSON, no other text.`;

    return this.generateContent(jsonPrompt).pipe(
      map(response => {
        // Try to extract JSON from markdown code blocks if present
        let jsonText = response.trim();
        const jsonMatch = jsonText.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
        if (jsonMatch) {
          jsonText = jsonMatch[1];
        }

        try {
          return JSON.parse(jsonText) as T;
        } catch (error) {
          console.error('Failed to parse JSON response:', response);
          throw new Error('Invalid JSON response from Gemini');
        }
      }),
      catchError(error => {
        console.error('Error generating JSON:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Check if Gemini service is properly configured
   * @returns boolean indicating if API key is set
   */
  isConfigured(): boolean {
    return !!environment.geminiApiKey && environment.geminiApiKey.length > 0;
  }
}
